import React, { useState } from 'react';
import { Search, RotateCcw, LocateFixed, Loader2, MapPinned } from 'lucide-react';
import { REGIONS, CATEGORIES, OFFERS } from '../data/mock';

const RADII = [5, 10, 25, 50, 100];

const Filters = ({ filters, setFilters, resultCount }) => {
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState('');

  const update = (key, value) => setFilters({ ...filters, [key]: value });
  const reset = () => setFilters({
    region: 'Alle Regionen', category: 'Alle', offer: '', query: '',
    near_city: '', near_lat: null, near_lng: null, radius_km: null,
  });

  const useMyLocation = () => {
    setGeoError('');
    if (!navigator.geolocation) {
      setGeoError('Geolocation nicht unterstützt');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFilters({
          ...filters,
          near_city: 'Mein Standort',
          near_lat: pos.coords.latitude,
          near_lng: pos.coords.longitude,
          radius_km: filters.radius_km || 25,
        });
        setLocating(false);
      },
      (err) => {
        const msg = err.code === 1
          ? 'Standortzugriff verweigert. Bitte in Browser-Einstellungen erlauben.'
          : err.code === 2 ? 'Standort nicht verfügbar.' : 'Zeitüberschreitung.';
        setGeoError(msg);
        setLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
  };

  const clearGeo = () => setFilters({
    ...filters, near_city: '', near_lat: null, near_lng: null, radius_km: null,
  });

  const geoActive = (filters.near_city || filters.near_lat !== null) && filters.radius_km;

  return (
    <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-5">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7a6b65]" />
          <input
            type="text"
            value={filters.query}
            onChange={(e) => update('query', e.target.value)}
            placeholder="Suche nach Name oder Beschreibung..."
            className="w-full bg-[#241a1f] border border-[#2a1f24] rounded-md pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-[#5a4d48] focus:outline-none focus:border-[#a83a5b]"
          />
        </div>
        <select
          value={filters.region}
          onChange={(e) => update('region', e.target.value)}
          className="md:col-span-3 bg-[#241a1f] border border-[#2a1f24] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#a83a5b]"
        >
          {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          value={filters.category}
          onChange={(e) => update('category', e.target.value)}
          className="md:col-span-3 bg-[#241a1f] border border-[#2a1f24] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#a83a5b]"
        >
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filters.offer}
          onChange={(e) => update('offer', e.target.value)}
          className="md:col-span-2 bg-[#241a1f] border border-[#2a1f24] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#a83a5b]"
        >
          <option value="">Angebote</option>
          {OFFERS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      {/* Distance search — unified block */}
      <div className="pt-4 mt-4 border-t border-[#2a1f24]">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h4 className="text-xs tracking-wider uppercase text-[#c19a8a] inline-flex items-center gap-1.5">
            <MapPinned className="w-3.5 h-3.5" /> Entfernungssuche
          </h4>
          <div className="flex items-center gap-2">
            <button type="button" onClick={useMyLocation} disabled={locating}
              className="text-xs px-3 py-1.5 rounded-md bg-[#241a1f] border border-[#2a1f24] text-[#c19a8a] hover:text-white hover:border-[#a83a5b] inline-flex items-center gap-1.5 disabled:opacity-60">
              {locating ? <Loader2 className="w-3 h-3 animate-spin" /> : <LocateFixed className="w-3 h-3" />}
              {locating ? 'Suche Standort...' : 'Meinen Standort nutzen'}
            </button>
            {geoActive && (
              <button onClick={clearGeo}
                className="text-xs text-[#9a8a83] hover:text-white inline-flex items-center gap-1">
                Entfernen
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {RADII.map((r) => (
            <button key={r} type="button"
              onClick={() => update('radius_km', r)}
              disabled={filters.near_lat === null && !filters.near_city}
              className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                filters.radius_km === r && (filters.near_city || filters.near_lat !== null)
                  ? 'bg-[#a83a5b] border-[#a83a5b] text-white'
                  : 'bg-[#241a1f] border-[#2a1f24] text-[#b8a9a3] hover:border-[#a83a5b] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed'
              }`}>
              {r} km
            </button>
          ))}
        </div>
        {geoError && <p className="text-xs text-[#ff8aa3] mt-2">{geoError}</p>}
        {geoActive && (
          <p className="text-xs text-[#c19a8a] mt-2">
            Zeige Inserate im Umkreis von <span className="text-white">{filters.radius_km} km</span>{' '}
            um <span className="text-white">{filters.near_city || 'deinen Standort'}</span>
          </p>
        )}
      </div>

      <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#2a1f24]">
        <p className="text-sm text-[#9a8a83]"><span className="text-white font-semibold">{resultCount}</span> Inserate gefunden</p>
        <button
          onClick={reset}
          className="inline-flex items-center gap-1.5 text-xs text-[#c19a8a] hover:text-white transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Alle Filter zurücksetzen
        </button>
      </div>
    </div>
  );
};

export default Filters;
